import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Cookies from 'js-cookie';

import LoadingAnimation from '@/components/LoadingAnimation';
import { AuthFlowSession } from '@/helpers/sessionUtils';
import { isValidJWT } from '@/helpers/tokenValidation';
import useAuth from '@/hooks/useAuth';

const AuthCallbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshCurrentUser, redirectToShopify } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const isShopify = AuthFlowSession.isShopify();

    if (accessToken && isValidJWT(accessToken)) {
      localStorage.setItem('access_token', accessToken);
      if (refreshToken && isValidJWT(refreshToken)) {
        localStorage.setItem('refresh_token', refreshToken);
      }

      // Set Shopify cookies if this is a Shopify authentication
      if (isShopify) {
        const shopDomain =
          (import.meta.env.VITE_SHOP_DOMAIN as string) || 'tapt.test';
        const env = (import.meta.env.VITE_SHOP_ENV as string) || 'local';
        Cookies.set(`${env}_access_token`, accessToken, {
          domain: shopDomain,
        });
        if (refreshToken) {
          Cookies.set(`${env}_refresh_token`, refreshToken, {
            domain: shopDomain,
          });
        }
      }

      // Refresh user data and redirect appropriately
      refreshCurrentUser()
        .then(() => {
          if (isShopify) {
            redirectToShopify();
          } else {
            navigate('/');
          }
        })
        .catch(error => {
          console.error('Failed to refresh user data:', error);

          // Redirect to appropriate login page with error indication
          if (isShopify) {
            navigate('/shopify/login?error=refresh_failed');
          } else {
            navigate('/login?error=refresh_failed');
          }
        })
        .finally(() => {
          AuthFlowSession.clear();
        });
    } else {
      // Clean up sessionStorage on error
      AuthFlowSession.clear();

      // Handle error case, redirect to appropriate login page
      if (isShopify) {
        navigate('/shopify/login?error=auth_failed');
      } else {
        navigate('/login?error=auth_failed');
      }
    }
  }, [location, navigate, refreshCurrentUser, redirectToShopify]);

  return <LoadingAnimation className="w-16 h-16 mx-auto text-brand-500" />;
};

export default AuthCallbackPage;
